class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  async updateQuantity(line, quantity, event, name, variantId) {
    const input = document.getElementById(`Drawer-quantity-${line}`);
    const groupVariantId = input?.dataset.ccoGroupVariantId || event.currentTarget?.dataset?.ccoGroupVariantId;
    if (!groupVariantId) {
      return super.updateQuantity(line, quantity, event, name, variantId);
    }

    this.enableLoading(line);
    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      if (!cartResponse.ok) throw new Error('Could not read the cart');
      const cart = await cartResponse.json();
      const group = cart.items.filter((item) =>
        String(item.variant_id) === groupVariantId &&
        !Object.keys(item.properties || {}).length &&
        !item.selling_plan_allocation
      );
      if (!group.length) throw new Error('Cart item was not found');

      const currentQuantity = group.reduce((total, item) => total + item.quantity, 0);
      const targetQuantity = Math.max(0, Number(quantity));
      const updates = {};
      if (targetQuantity >= currentQuantity) {
        updates[group[0].key] = group[0].quantity + targetQuantity - currentQuantity;
      } else {
        let toRemove = currentQuantity - targetQuantity;
        for (const item of [...group].reverse()) {
          const remove = Math.min(toRemove, item.quantity);
          if (remove) updates[item.key] = item.quantity - remove;
          toRemove -= remove;
          if (!toRemove) break;
        }
      }

      if (Object.keys(updates).length) {
        const body = JSON.stringify({
          updates,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });
        const response = await fetch(`${routes.cart_url}/update.js`, { ...fetchConfig(), body });
        const parsedState = await response.json();
        if (!response.ok || parsedState.errors) {
          throw new Error(parsedState.description || parsedState.message || window.cartStrings.error);
        }

        // Shopify can return section HTML from before automatic discounts finish recalculating.
        const freshCartResponse = await fetch(`${routes.cart_url}.js`, { cache: 'no-store' });
        const freshSectionsResponse = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble&_=${Date.now()}`, { cache: 'no-store' });
        if (!freshCartResponse.ok || !freshSectionsResponse.ok) throw new Error(window.cartStrings.error);
        const freshCart = await freshCartResponse.json();
        const freshSections = await freshSectionsResponse.json();
        parsedState.item_count = freshCart.item_count;
        parsedState.sections = freshSections;

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', parsedState.item_count === 0);
        this.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container || !parsedState.sections[section.section]) return;
          const target = container.querySelector(section.selector) || container;
          target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
        const drawer = document.querySelector('cart-drawer');
        const nextInput = document.getElementById(`Drawer-quantity-${line}`);
        if (drawer && nextInput && name) {
          trapFocus(drawer, nextInput.closest('quantity-input')?.querySelector(`[name="${name}"]`) || nextInput);
        } else if (drawer && parsedState.item_count === 0) {
          trapFocus(drawer.querySelector('.drawer__inner-empty'), drawer.querySelector('a'));
        }
      }
    } catch (error) {
      if (input) input.value = input.getAttribute('value');
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = error.message || window.cartStrings.error;
    } finally {
      this.disableLoading(line);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// ----------------------
// FREE GIFT CLAIM HANDLER
// ----------------------
document.addEventListener('click', async function (e) {
  if (!e.target.matches('#claim-gifts-btn')) return;

  e.target.disabled = true;
  e.target.innerText = 'Adding...';
  if (e.target.classList.contains('processing')) return;
e.target.classList.add('processing');

  // List of handles coming from Liquid window object
  const gifts = window.freeGiftHandles || [];
  if (gifts.length === 0) return;

  // Add each variant to cart one at a time
for (let variantId of window.freeGiftVariants) {
  if (!variantId) continue;

    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }]
      })
    });
  }

  // --- Refresh Drawer ---
fetch('/?sections=cart-drawer,cart-icon-bubble')
  .then(res => res.json())
  .then(data => {
    // Update Cart Drawer content
    const drawerHTML = data['cart-drawer'];
    const parser = new DOMParser();
    const doc = parser.parseFromString(drawerHTML, 'text/html');

    const newInner = doc.querySelector('.drawer__inner');
    const currentInner = document.querySelector('#CartDrawer .drawer__inner');
    if (newInner && currentInner) {
      currentInner.innerHTML = newInner.innerHTML;
    }

    // Update cart bubble
    const bubbleHTML = data['cart-icon-bubble'];
    const bubbleDoc = parser.parseFromString(bubbleHTML, 'text/html');
    const newBubble = bubbleDoc.querySelector('.shopify-section');
    const currentBubble = document.querySelector('#cart-icon-bubble');
    if (newBubble && currentBubble) {
      currentBubble.innerHTML = newBubble.innerHTML;
    }

    // Re-open cart drawer (keeps it visible after refresh)
    const drawer = document.querySelector('cart-drawer');
    drawer?.open();

    // Reset button UI
    const btn = document.querySelector('#claim-gifts-btn');
    if (btn) btn.remove(); // removes button once gifts added
  });

});
